/**
 * Cookie Consent Service — North American defaults
 *
 * Operating jurisdictions: US, Canada, Mexico (no EU/UK presence yet).
 *
 * Implements Google Tag Manager Consent Mode v2 with jurisdiction-aware
 * defaults:
 *
 *   Non-Quebec visitors (US, MX, rest of Canada):
 *   - analytics_storage: GRANTED by default (implied consent — opt-out model)
 *   - marketing/ads:    DENIED by default (opt-in required)
 *
 *   Quebec visitors (Loi 25 explicit consent regime):
 *   - All non-essential tracking DENIED by default (opt-in required)
 *
 * Quebec detection is approximate (language + stored locale) and runs
 * pre-React in public/index.html — keep that block in sync with
 * isQuebecVisitor() below.
 *
 * Legal basis:
 * - US: CCPA/CPRA + state privacy laws use opt-out for analytics
 * - MX: LFPDPPP accepts tacit consent for non-sensitive data
 * - CA federal (PIPEDA): implied consent for disclosed analytics
 * - CA Quebec (Loi 25): explicit consent required — handled separately
 *
 * Implementation features:
 * - Granular categories (necessary, analytics, marketing)
 * - Consent stored in both cookie and localStorage for reliability
 * - 12-month expiration on stored consent
 * - Consent versioning for policy updates
 * - User can withdraw consent at any time via footer "Manage Cookies"
 */

// Current consent policy version - increment when privacy policy changes
export const CONSENT_VERSION = "1.0";

// Consent expiration in days (GDPR recommends max 12 months)
const CONSENT_EXPIRY_DAYS = 365;

// Cookie name for consent storage
const CONSENT_COOKIE_NAME = "cm_cookie_consent";

/**
 * Consent categories with descriptions
 */
export const CONSENT_CATEGORIES = {
  necessary: {
    id: "necessary",
    name: "Necessary",
    description: "Essential cookies required for the website to function. These cannot be disabled.",
    required: true,
  },
  analytics: {
    id: "analytics",
    name: "Analytics",
    description: "Help us understand how visitors interact with our website by collecting anonymous information.",
    required: false,
  },
  marketing: {
    id: "marketing",
    name: "Marketing",
    description: "Used to deliver personalized advertisements and measure the effectiveness of ad campaigns.",
    required: false,
  },
};

/**
 * Approximate Quebec visitor detection.
 *
 * Returns true if any browser/stored language signal indicates fr-CA.
 * Used to fall back to GDPR-style opt-in for Quebec residents while
 * letting the rest of North America operate on implied consent.
 *
 * NOTE: keep in sync with the inline isQuebecVisitor() in
 * public/index.html — both must agree on the same defaults to avoid
 * a flash of wrong consent state.
 */
export function isQuebecVisitor() {
  try {
    const langs = [];
    if (typeof navigator !== "undefined") {
      if (navigator.language) langs.push(navigator.language);
      if (navigator.languages) langs.push(...navigator.languages);
    }
    if (typeof localStorage !== "undefined") {
      const stored = localStorage.getItem("i18nextLng") || localStorage.getItem("lang");
      if (stored) langs.push(stored);
    }
    return langs.some((lang) => lang && lang.toLowerCase().startsWith("fr-ca"));
  } catch {
    return false;
  }
}

/**
 * Default consent state.
 *
 * Non-QC: analytics granted (opt-out model, legal in US/MX/non-QC CA).
 * QC:     analytics denied (Loi 25 explicit opt-in required).
 * Everyone: marketing denied (opt-in required regardless of jurisdiction).
 */
function getDefaultConsent() {
  return {
    necessary: true,             // Always required
    analytics: !isQuebecVisitor(), // Granted by default for non-QC
    marketing: false,            // Always denied by default
  };
}

const DEFAULT_CONSENT = getDefaultConsent();

/**
 * Set a cookie with proper attributes
 */
function setCookie(name, value, days) {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;

  // SameSite=Lax for security, Secure in production
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; ${expires}; path=/; SameSite=Lax${secure}`;
}

/**
 * Get a cookie value by name
 */
function getCookie(name) {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(";");

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
}

/**
 * Delete a cookie
 */
function deleteCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
}

/**
 * Initialize GTM Consent Mode v2 with default denied state
 * This MUST be called before GTM loads
 */
export function initializeConsentMode() {
  // Initialize dataLayer if not exists
  window.dataLayer = window.dataLayer || [];

  // Define gtag function for consent commands
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  // Set default consent state.
  // Non-QC: analytics granted (implied consent, opt-out model).
  // QC:     analytics denied (Loi 25 opt-in required).
  // Marketing categories: always denied by default — opt-in required.
  // This must happen BEFORE GTM loads.
  const inQuebec = isQuebecVisitor();
  gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: inQuebec ? "denied" : "granted",
    functionality_storage: "granted", // Necessary cookies
    personalization_storage: "denied",
    security_storage: "granted", // Necessary for security
    wait_for_update: 500, // Wait 500ms for consent update
  });

  // Enable URL passthrough for better analytics without cookies
  gtag("set", "url_passthrough", true);

  // Enable ads data redaction when consent denied
  gtag("set", "ads_data_redaction", true);

  console.log(
    `[CookieConsent] Consent Mode v2 initialized — analytics ${inQuebec ? "denied (QC)" : "granted (non-QC implied)"}; marketing denied`
  );
}

/**
 * Update GTM consent state based on user preferences
 */
export function updateConsentMode(consent) {
  if (!window.gtag) {
    console.warn("[CookieConsent] gtag not available, cannot update consent");
    return;
  }

  const consentUpdate = {
    // Analytics consent
    analytics_storage: consent.analytics ? "granted" : "denied",

    // Marketing/Advertising consent
    ad_storage: consent.marketing ? "granted" : "denied",
    ad_user_data: consent.marketing ? "granted" : "denied",
    ad_personalization: consent.marketing ? "granted" : "denied",
    personalization_storage: consent.marketing ? "granted" : "denied",

    // Always granted for necessary functionality
    functionality_storage: "granted",
    security_storage: "granted",
  };

  window.gtag("consent", "update", consentUpdate);

  // Push consent event to dataLayer for GTM triggers
  window.dataLayer.push({
    event: "consent_update",
    consent_analytics: consent.analytics,
    consent_marketing: consent.marketing,
  });

  console.log("[CookieConsent] Consent updated:", consentUpdate);
}

/**
 * Load GTM script dynamically (only after consent setup)
 */
export function loadGTM(gtmId) {
  if (!gtmId) {
    console.warn("[CookieConsent] No GTM ID provided");
    return;
  }

  // Check if GTM is already loaded
  if (window.google_tag_manager && window.google_tag_manager[gtmId]) {
    console.log("[CookieConsent] GTM already loaded");
    return;
  }

  // Load GTM script
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;

  script.onload = () => {
    console.log("[CookieConsent] GTM loaded successfully");
  };

  script.onerror = () => {
    console.error("[CookieConsent] Failed to load GTM");
  };

  // Insert GTM script
  const firstScript = document.getElementsByTagName("script")[0];
  firstScript.parentNode.insertBefore(script, firstScript);

  // Push GTM start event
  window.dataLayer.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });
}

/**
 * Get stored consent from cookie/localStorage
 * Returns null if no consent has been given
 */
export function getStoredConsent() {
  // Try cookie first (more reliable for server-side)
  const cookieValue = getCookie(CONSENT_COOKIE_NAME);

  if (cookieValue) {
    try {
      const consent = JSON.parse(cookieValue);

      // Check if consent has expired
      if (consent.expiresAt && new Date(consent.expiresAt) < new Date()) {
        console.log("[CookieConsent] Consent expired, requiring new consent");
        clearConsent();
        return null;
      }

      // Check if consent version matches (require re-consent on policy update)
      if (consent.version !== CONSENT_VERSION) {
        console.log("[CookieConsent] Consent version mismatch, requiring new consent");
        clearConsent();
        return null;
      }

      return consent;
    } catch (e) {
      console.error("[CookieConsent] Failed to parse consent cookie:", e);
    }
  }

  // Fallback to localStorage (for backwards compatibility)
  const localValue = localStorage.getItem("cookieConsent");
  if (localValue) {
    try {
      const consent = JSON.parse(localValue);
      // Migrate old consent to new format
      if (!consent.version) {
        console.log("[CookieConsent] Migrating old consent format");
        return null; // Require fresh consent for proper compliance
      }
      return consent;
    } catch (e) {
      console.error("[CookieConsent] Failed to parse localStorage consent:", e);
    }
  }

  return null;
}

/**
 * Save consent preferences
 */
export function saveConsent(preferences) {
  const consent = {
    // Consent preferences
    necessary: true, // Always true
    analytics: Boolean(preferences.analytics),
    marketing: Boolean(preferences.marketing),

    // Metadata for compliance
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    expiresAt: new Date(Date.now() + CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000).toISOString(),

    // Record how consent was given
    method: preferences.method || "banner", // banner, settings, or programmatic
    userAgent: navigator.userAgent,
  };

  // Save to cookie (primary storage)
  setCookie(CONSENT_COOKIE_NAME, JSON.stringify(consent), CONSENT_EXPIRY_DAYS);

  // Also save to localStorage (backup and for easy JS access)
  localStorage.setItem("cookieConsent", JSON.stringify(consent));

  // Update GTM consent state
  updateConsentMode(consent);

  console.log("[CookieConsent] Consent saved:", consent);

  // Dispatch custom event for other components to react
  window.dispatchEvent(new CustomEvent("consentUpdated", { detail: consent }));

  return consent;
}

/**
 * Clear all consent (for testing or user request)
 */
export function clearConsent() {
  deleteCookie(CONSENT_COOKIE_NAME);
  localStorage.removeItem("cookieConsent");

  // Reset GTM consent to denied
  updateConsentMode(DEFAULT_CONSENT);

  console.log("[CookieConsent] Consent cleared");
}

/**
 * Check if user has given any consent (accepted or rejected)
 */
export function hasConsentDecision() {
  return getStoredConsent() !== null;
}

/**
 * Check if specific consent category is granted.
 *
 * If the user has actively saved consent (via banner or settings), the
 * stored value wins. If they haven't, fall back to DEFAULT_CONSENT —
 * which is jurisdiction-aware (analytics granted for non-QC, denied
 * for QC; marketing denied everywhere).
 */
export function hasConsent(category) {
  if (category === "necessary") return true; // Always granted

  const consent = getStoredConsent();
  if (consent) return Boolean(consent[category]);

  // No stored decision yet — apply jurisdiction-aware defaults.
  return Boolean(DEFAULT_CONSENT[category]);
}

/**
 * Accept all cookies
 */
export function acceptAll(method = "banner") {
  return saveConsent({
    analytics: true,
    marketing: true,
    method,
  });
}

/**
 * Reject all optional cookies (only necessary allowed)
 */
export function rejectAll(method = "banner") {
  return saveConsent({
    analytics: false,
    marketing: false,
    method,
  });
}

/**
 * Open consent preferences (for "Manage Cookies" link)
 */
export function openConsentPreferences() {
  window.dispatchEvent(new CustomEvent("openCookieSettings"));
}

/**
 * Initialize the consent system
 * Call this as early as possible in your app
 */
export function initializeCookieConsent(gtmId) {
  // 1. Initialize consent mode with defaults BEFORE anything else
  initializeConsentMode();

  // 2. Check for existing consent
  const existingConsent = getStoredConsent();

  if (existingConsent) {
    // User has previously consented - apply their preferences
    console.log("[CookieConsent] Found existing consent:", existingConsent);
    updateConsentMode(existingConsent);
  } else {
    console.log("[CookieConsent] No existing consent, waiting for user decision");
  }

  // 3. Load GTM (it will respect consent mode)
  if (gtmId) {
    loadGTM(gtmId);
  }

  return existingConsent;
}

// Export default consent for reference
export { DEFAULT_CONSENT };
